import React, { useState, useEffect, useRef } from 'react';
import { getColorsData } from '../services/api';
import './ColorDropdown.css';
import { matchesSearch } from '../utils/search';

const ColorDropdown = ({ value, onChange, placeholder = "Select color..." }) => {
  const [colors, setColors] = useState([]);
  const [filteredColors, setFilteredColors] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch colors on component mount
  useEffect(() => {
    const fetchColors = async () => {
      setLoading(true);
      try {
        const response = await getColorsData();
        const colorsData = response.data || {};
        
        // Convert object to array of {key, value} pairs
        const colorsArray = Object.entries(colorsData).map(([key, value]) => ({
          key,
          value
        }));
        
        setColors(colorsArray);
        setFilteredColors(colorsArray);
      } catch (error) {
        console.error('Error fetching colors:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchColors();
  }, []);

  // Filter colors based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredColors(colors);
    } else {
      const filtered = colors.filter(color => 
        matchesSearch(searchTerm, color.key, color.value)
      );
      setFilteredColors(filtered);
    }
  }, [searchTerm, colors]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (color) => {
    onChange(color.value);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const getDisplayValue = () => {
    if (!value) return '';
    const selectedColor = colors.find(color => color.value === value);
    return selectedColor ? `${selectedColor.key} (${selectedColor.value})` : value;
  };

  return (
    <div className="color-dropdown" ref={dropdownRef}>
      <input
        type="text"
        value={isOpen ? searchTerm : getDisplayValue()}
        onChange={handleInputChange}
        onClick={handleInputClick}
        placeholder={placeholder}
        className="color-dropdown-input"
        readOnly={!isOpen}
      />
      {isOpen && (
        <div className="color-dropdown-menu">
          {loading ? (
            <div className="dropdown-loading">Loading colors...</div>
          ) : filteredColors.length === 0 ? (
            <div className="dropdown-no-results">No colors found</div>
          ) : (
            filteredColors.map((color) => (
              <div
                key={color.key}
                className="dropdown-option"
                onClick={() => handleSelect(color)}
              >
                <div className="color-key">{color.key}</div>
                <div className="color-value">{color.value}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ColorDropdown;
